import { backupLinkSchema, backupSchema, hasPermission, type Backup } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Archive,
  Clock,
  Cloud,
  Download,
  HardDrive,
  Loader2,
  Lock,
  LockOpen,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { StatTile } from '@/components/stat-tile';
import { StatusPill } from '@/components/status-badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { bytes, dateTime, timeAgo } from '@/lib/format';
import { newestBackup } from '@/lib/needs-attention';
import { backupListQuery, serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/backups')({ component: BackupsTab });

function BackupsTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const queryClient = useQueryClient();
  const { queryKey: key, ...listQuery } = backupListQuery(id);
  const list = useQuery({
    queryKey: key,
    ...listQuery,
    // Poll while something is running so progress appears without a refresh.
    refetchInterval: (q) =>
      q.state.data?.data.some((b) => b.isSuccessful === null) ||
      server.status === 'restoring_backup'
        ? 2000
        : false,
  });
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState<Backup | null>(null);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: serverQuery(id).queryKey });
  };

  const toggleLock = useMutation({
    mutationFn: (b: Backup) =>
      apiRequest(`/api/client/servers/${id}/backups/${b.uuid}/lock`, {
        method: 'POST',
        schema: backupSchema,
      }),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (b: Backup) =>
      apiRequest(`/api/client/servers/${id}/backups/${b.uuid}`, {
        method: 'DELETE',
        schema: z.null(),
      }),
    onSuccess: () => {
      toast.success('Backup deleted.');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const download = useMutation({
    mutationFn: (b: Backup) =>
      apiRequest(`/api/client/servers/${id}/backups/${b.uuid}/download`, {
        schema: backupLinkSchema,
      }),
    onSuccess: (link) => {
      window.location.assign(link.url);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const busy = server.status !== null;
  const totalBytes = (list.data?.data ?? []).reduce(
    (n, b) => n + (b.isSuccessful ? b.bytes : 0),
    0,
  );
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">Backups of the server’s files.</p>
        {can('backup.create') && (
          <Button onClick={() => setCreating(true)} disabled={busy || list.data?.limit === 0}>
            <Plus /> New backup
          </Button>
        )}
      </div>
      {list.data && list.data.limit > 0 && (
        <BackupSummary backups={list.data.data} limit={list.data.limit} totalBytes={totalBytes} />
      )}
      {server.status === 'restoring_backup' && (
        <p className="text-warning flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> A backup is being restored. The server can’t
          be used until that finishes.
        </p>
      )}

      <QueryState query={list}>
        {({ data, limit }) =>
          limit === 0 ? (
            <Empty
              title="Backups are turned off"
              body="An administrator hasn’t given this server any backup slots."
            />
          ) : data.length === 0 ? (
            <Empty title="No backups yet" body="Create one before you change something risky." />
          ) : (
            <ul className="grid gap-3">
              {data.map((b) => (
                <li key={b.uuid}>
                  <Card className="flex-row flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div className="grid min-w-0 gap-1">
                      <p className="flex flex-wrap items-center gap-2 font-medium">
                        <span className="truncate">{b.name}</span>
                        {b.isLocked && (
                          <Lock className="text-muted-foreground size-3.5" aria-label="Locked" />
                        )}
                        <Status backup={b} />
                      </p>
                      <p className="text-muted-foreground flex flex-wrap items-center gap-x-3 text-xs">
                        <span className="flex items-center gap-1">
                          {b.disk === 's3' ? (
                            <Cloud className="size-3.5" />
                          ) : (
                            <HardDrive className="size-3.5" />
                          )}
                          {b.disk === 's3' ? 'S3' : 'This node'}
                        </span>
                        {b.isSuccessful && <span>{bytes(b.bytes)}</span>}
                        <span title={b.createdAt}>{timeAgo(b.createdAt)}</span>
                        {b.checksum && (
                          <span className="font-mono" title={`SHA-256 ${b.checksum}`}>
                            {b.checksum.slice(0, 8)}
                          </span>
                        )}
                      </p>
                    </div>
                    {b.isSuccessful !== null && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={`Actions for ${b.name}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {b.isSuccessful && can('backup.download') && (
                            <DropdownMenuItem onSelect={() => download.mutate(b)}>
                              <Download /> Download
                            </DropdownMenuItem>
                          )}
                          {b.isSuccessful && can('backup.restore') && (
                            <DropdownMenuItem disabled={busy} onSelect={() => setRestoring(b)}>
                              <RotateCcw /> Restore…
                            </DropdownMenuItem>
                          )}
                          {can('backup.delete') && (
                            <>
                              <DropdownMenuItem onSelect={() => toggleLock.mutate(b)}>
                                {b.isLocked ? <LockOpen /> : <Lock />}{' '}
                                {b.isLocked ? 'Unlock' : 'Lock'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <Confirm
                                title={`Delete “${b.name}”?`}
                                description="The backup is removed for good."
                                action="Delete"
                                destructive
                                onConfirm={() => remove.mutate(b)}
                              >
                                <DropdownMenuItem
                                  disabled={b.isLocked}
                                  onSelect={(e) => e.preventDefault()}
                                  className="text-destructive"
                                >
                                  <Trash2 /> Delete
                                </DropdownMenuItem>
                              </Confirm>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>

      {creating && <CreateDialog server={id} onClose={() => setCreating(false)} onDone={refresh} />}
      {restoring && (
        <RestoreDialog
          server={id}
          backup={restoring}
          onClose={() => setRestoring(null)}
          onDone={refresh}
        />
      )}
    </div>
  );
}

function Status({ backup }: { backup: Backup }) {
  if (backup.isSuccessful === null)
    return (
      <StatusPill tone="warning" pulse>
        Running
      </StatusPill>
    );
  return backup.isSuccessful ? (
    <StatusPill tone="success">Complete</StatusPill>
  ) : (
    <StatusPill tone="destructive">Failed</StatusPill>
  );
}

/** Slots, space and age at a glance; each tile only says what the list really shows. */
function BackupSummary({
  backups,
  limit,
  totalBytes,
}: {
  backups: Backup[];
  limit: number;
  totalBytes: number;
}) {
  const newest = newestBackup(backups);
  const running = backups.filter((b) => b.isSuccessful === null).length;
  return (
    <section aria-label="Backup summary" className="grid gap-3 sm:grid-cols-3">
      <StatTile
        icon={Archive}
        label="Slots"
        value={backups.length}
        limit={`of ${limit}`}
        fraction={backups.length / limit}
        detail={backups.length >= limit ? 'All slots are used' : `${limit - backups.length} free`}
      />
      <StatTile
        icon={HardDrive}
        label="Stored"
        value={bytes(totalBytes)}
        detail="Successful backups, wherever they are stored"
      />
      <StatTile
        icon={Clock}
        label="Newest"
        value={newest ? timeAgo(newest.toISOString()) : '—'}
        detail={
          running > 0
            ? `${running} running now`
            : newest
              ? dateTime(newest.toISOString())
              : 'No successful backup yet'
        }
      />
    </section>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <Card className="bg-card/50 border-dashed shadow-none">
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <span className="bg-elevated text-muted-foreground grid size-11 place-items-center rounded-lg border">
          <Archive className="size-6" aria-hidden />
        </span>
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground max-w-sm text-sm">{body}</p>
      </CardContent>
    </Card>
  );
}

function CreateDialog({
  server,
  onClose,
  onDone,
}: {
  server: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [ignored, setIgnored] = useState('');
  const create = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/backups`, {
        method: 'POST',
        body: { name: name || undefined, ignored },
        schema: backupSchema,
      }),
    onSuccess: () => {
      toast.success('Backup started.');
      onDone();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New backup</DialogTitle>
          <DialogDescription>
            Everything in the server’s folder is included, except what you list below and anything
            in a <code className="font-mono">.hearthignore</code> file.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <FormField label="Name (optional)">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={191}
              placeholder="Before updating plugins"
            />
          </FormField>
          <FormField
            label="Files to leave out"
            description="One pattern per line, like .gitignore (for example logs/ or *.tmp)."
          >
            <Textarea
              value={ignored}
              onChange={(e) => setIgnored(e.target.value)}
              rows={4}
              className="font-mono text-xs"
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Starting…' : 'Create backup'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RestoreDialog({
  server,
  backup,
  onClose,
  onDone,
}: {
  server: string;
  backup: Backup;
  onClose: () => void;
  onDone: () => void;
}) {
  const [truncate, setTruncate] = useState(false);
  const restore = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/backups/${backup.uuid}/restore`, {
        method: 'POST',
        body: { truncate },
        schema: z.null(),
      }),
    onSuccess: () => {
      toast.success('Restoring. The server is stopped until it finishes.');
      onDone();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restore “{backup.name}”?</DialogTitle>
          <DialogDescription>
            The server is stopped and its files are replaced with the ones in this backup, taken{' '}
            {timeAgo(backup.createdAt)}. Files with the same name are overwritten.
          </DialogDescription>
        </DialogHeader>
        <p className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          Anything changed since this backup, such as player progress and new builds, is lost. Make
          a new backup first if you might want the current state.
        </p>
        <div className="flex items-start gap-2">
          <Checkbox
            id="truncate"
            checked={truncate}
            onCheckedChange={(v) => setTruncate(v === true)}
          />
          <Label htmlFor="truncate" className="grid gap-0.5">
            Delete everything else first
            <span className="text-muted-foreground text-xs font-normal">
              Files that aren’t in the backup are removed, so the server matches it exactly.
            </span>
          </Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => restore.mutate()}
            disabled={restore.isPending}
          >
            {restore.isPending ? 'Stopping server…' : 'Restore backup'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
